import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react";
import './index.css'
import App from './App.jsx'

import { AuthProvider } from './context/AuthContext.jsx'

// Initialize Sentry before React
// Initialize Sentry before React
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn && sentryDsn.startsWith('https')) {
  try {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',

      // Performance Monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true, // Privacy: mask all user text
          blockAllMedia: true, // Privacy: block media
        }),
      ],

      // Performance sample rate
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev

      // Session Replay sample rate
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% when error occurs

      // Only enable in production or when DSN is set
      enabled: import.meta.env.PROD || import.meta.env.DEV,

      // Release tracking
      release: `keyers-journey@${import.meta.env.VITE_APP_VERSION || 'dev'}`,

      // Ignore common non-critical errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension',
        'moz-extension',
        // Network errors (expected on poor connections)
        'Network request failed',
        'Failed to fetch',
        'NetworkError',
        // ResizeObserver (harmless)
        'ResizeObserver loop limit exceeded',
      ],

      // Filter sensitive data
      beforeSend(event, hint) {
        // Filter out sensitive data
        if (event.request) {
          delete event.request.cookies;
          if (event.request.headers) {
            delete event.request.headers['Authorization'];
            delete event.request.headers['Cookie'];
          }
        }

        return event;
      },
    });
  } catch (e) {
    console.warn('Sentry initialization failed:', e);
  }
} else if (sentryDsn) {
  console.warn('Invalid Sentry DSN provided. Skipping initialization.');
}

import { initAnalytics } from './utils/analytics';
import { registerSW } from 'virtual:pwa-register';

// Initialize Analytics
initAnalytics();

// Register Service Worker
registerSW({ immediate: true });

import { HelmetProvider } from 'react-helmet-async';

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <HelmetProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </HelmetProvider>
  </StrictMode>,
)
